/**
 * brochureVectorMinimal.js — Layout MINIMAL in vector PDF (jsPDF).
 *
 * Stile: whitespace generoso, geometrico, stile manifesto svizzero/Vignelli.
 * Allineamenti rigorosi, niente ornamenti, font sans light.
 * jsPDF non ha Space Grotesk → usiamo helvetica normal/bold come fallback.
 */
import { A4_W, A4_H, setFill, setStroke, setText, safe, getAttrs, cleanCity, loadImageAsJpegDataUrl, loadAgencyLogo, addPhotoCover, shortenEmail, cityIfNotInTitle } from "./brochurePdfShared";

export async function buildCoverMinimal(pdf, data, col, showPrice = true) {
  // Sfondo bianco totale
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // Foto rettangolo grande con margine generoso (manifesto svizzero)
  const margin = 18;
  const photoY = 56;
  const photoH = 150;
  const photoW = A4_W - 2 * margin;
  // iter v28 r16 — alta risoluzione + cover-fit
  const hero = await loadImageAsJpegDataUrl(data.photos.hero, 2400);
  if (hero) await addPhotoCover(pdf, hero, margin, photoY, photoW, photoH);
  else { setFill(pdf, "#F1F4ED"); pdf.rect(margin, photoY, photoW, photoH, "F"); }

  // Top row: logo sx + numero immobile dx
  // iter v28r8: logo ingrandito 16 → 22mm
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, false);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(22 / lw, 22 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", margin, 14, dw, dh);
    }
  }
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.55 }));
  const ag = safe(data.agencyName, "").toUpperCase();
  if (ag) pdf.text(ag, margin + (data.agencyLogoUrl ? 20 : 0), 26);
  // Dx: "—01" piccolo
  const num = "—01";
  const nw = pdf.getTextWidth(num);
  pdf.text(num, A4_W - margin - nw, 26);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Bottom: titolo + indirizzo + prezzo (allineato a sinistra, stile manifesto)
  let by = photoY + photoH + 16;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  setText(pdf, col.accent);
  pdf.text("IMMOBILE", margin, by);
  by += 8;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(18);
  setText(pdf, "#1C1C1A");
  const title = safe(data.title, "Immobile");
  const titleLines = pdf.splitTextToSize(title, A4_W - 2 * margin);
  titleLines.slice(0, 2).forEach((line) => {
    pdf.text(line, margin, by);
    by += 7;
  });
  by += 1;

  // Città piccola (solo se non duplicata nel titolo)
  const cityShow = cityIfNotInTitle(data);
  if (cityShow) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    setText(pdf, "#1C1C1A");
    pdf.setGState(new pdf.GState({ opacity: 0.6 }));
    pdf.text(cityShow, margin, by + 2);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Prezzo destra
  if (showPrice) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    setText(pdf, "#1C1C1A");
    const p = safe(data.price, "—");
    const pw = pdf.getTextWidth(p);
    pdf.text(p, A4_W - margin - pw, by + 2);
  }

  // Meta sotto entrambi (linea sottile)
  const lineY = by + 8;
  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.2);
  pdf.setGState(new pdf.GState({ opacity: 0.3 }));
  pdf.line(margin, lineY, A4_W - margin, lineY);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.7 }));
  const meta = `${data.surface || "—"} mq · ${data.rooms || "—"} locali · ${data.bathrooms || "—"} bagni`;
  pdf.text(meta, margin, lineY + 5);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}

export async function buildDescriptionMinimal(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  const margin = 18;
  // Header semplice
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("LA PROPRIETÀ", margin, 24);
  pdf.text("02 / 04", A4_W - margin - pdf.getTextWidth("02 / 04"), 24);

  // Linea sottile
  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.2);
  pdf.setGState(new pdf.GState({ opacity: 0.3 }));
  pdf.line(margin, 28, A4_W - margin, 28);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Layout 2 colonne: body sx 65%, sidebar attributi dx 35%
  const bodyW = (A4_W - 2 * margin) * 0.62;
  const sideX = margin + bodyW + 6;
  const sideW = A4_W - margin - sideX;

  // Title big light
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(20);
  setText(pdf, "#1C1C1A");
  const title = safe(data.title, "Immobile");
  const titleLines = pdf.splitTextToSize(title, bodyW);
  let ty = 44;
  titleLines.slice(0, 2).forEach((line) => {
    pdf.text(line, margin, ty);
    ty += 8;
  });

  // Description body
  const desc = (data.description || data.subtitle || "").trim();
  if (desc) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    setText(pdf, "#1C1C1A");
    const wrapped = pdf.splitTextToSize(desc.length > 700 ? desc.slice(0, 697) + "…" : desc, bodyW);
    let dy = ty + 6;
    wrapped.forEach((line) => {
      if (dy > A4_H - 30) return;
      pdf.text(line, margin, dy);
      dy += 5.2;
    });
  }

  // Sidebar attributi con bordo sinistro accent
  const attrs = getAttrs(data);
  setStroke(pdf, col.accent);
  pdf.setLineWidth(0.6);
  pdf.line(sideX - 4, 44, sideX - 4, 44 + attrs.length * 14);
  attrs.forEach(([k, v], i) => {
    const y = 50 + i * 14;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    setText(pdf, col.accent);
    pdf.text(k, sideX, y);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setText(pdf, "#1C1C1A");
    pdf.text(safe(v), sideX, y + 6);
  });

  drawFooterMinimal(pdf, data, "02");
}

export async function buildGalleryMinimal(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  const margin = 18;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("GALLERIA", margin, 24);
  pdf.text("03 / 04", A4_W - margin - pdf.getTextWidth("03 / 04"), 24);

  // Grid 2×2 perfettamente simmetrico
  const urls = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4].filter(Boolean);
  // iter v28 r16 — bump risoluzione 1400→2000 + cover-fit
  const photos = await Promise.all(urls.map((u) => loadImageAsJpegDataUrl(u, 2000)));
  const gridTop = 36;
  const gridBottom = A4_H - 24;
  const gridH = gridBottom - gridTop;
  const g = 5;
  const cw = (A4_W - 2 * margin - g) / 2;
  const ch = (gridH - g) / 2;
  const positions = [[margin, gridTop], [margin + cw + g, gridTop], [margin, gridTop + ch + g], [margin + cw + g, gridTop + ch + g]];
  for (let i = 0; i < 4; i++) {
    const ph = photos[i];
    const [x, y] = positions[i];
    if (ph) await addPhotoCover(pdf, ph, x, y, cw, ch);
    else {
      setFill(pdf, "#F1F4ED");
      pdf.rect(x, y, cw, ch, "F");
    }
  }

  drawFooterMinimal(pdf, data, "03");
}

export async function buildContactsMinimal(pdf, data, col, showProntoLabel = true) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  const margin = 18;
  // Top label
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("CONTATTI", margin, 24);
  pdf.text("04 / 04", A4_W - margin - pdf.getTextWidth("04 / 04"), 24);

  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.2);
  pdf.setGState(new pdf.GState({ opacity: 0.3 }));
  pdf.line(margin, 28, A4_W - margin, 28);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Logo + nome agenzia sx
  // iter v28r8: logo ingrandito 28 → 38mm
  let agStartY = 52;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, false);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(38 / lw, 38 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", margin, 40, dw, dh);
      agStartY = 40 + dh + 8;
    }
  }
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(22);
  setText(pdf, "#1C1C1A");
  const ag = safe(data.agencyName, "La tua agenzia");
  const agLines = pdf.splitTextToSize(ag, A4_W - 2 * margin);
  agLines.slice(0, 2).forEach((line, i) => {
    pdf.text(line, margin, agStartY + i * 8);
  });
  if (data.agencyWeb) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    setText(pdf, "#1C1C1A");
    pdf.setGState(new pdf.GState({ opacity: 0.6 }));
    pdf.text(data.agencyWeb, margin, agStartY + agLines.length * 8 + 4);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Linea divider centrale
  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.2);
  pdf.setGState(new pdf.GState({ opacity: 0.3 }));
  pdf.line(margin, A4_H / 2 - 6, A4_W - margin, A4_H / 2 - 6);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Agent section
  const agentY = A4_H / 2 + 8;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("AGENTE", margin, agentY);

  // Agent photo + nome
  let nameX = margin;
  if (data.agentPhoto) {
    // iter v28 r16 — bump risoluzione + cover-fit (centra volto)
    const ph = await loadImageAsJpegDataUrl(data.agentPhoto, 900);
    if (ph) {
      const r = 14;
      const cx = margin + r;
      const cy = agentY + 10 + r;
      setFill(pdf, "#F1F4ED");
      pdf.circle(cx, cy, r, "F");
      const innerSize = (r - 1.5) * 2;
      await addPhotoCover(pdf, ph, cx - r + 1.5, cy - r + 1.5, innerSize, innerSize, { anchor: "top" });
      nameX = cx + r + 5;
    }
  }
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(20);
  setText(pdf, "#1C1C1A");
  pdf.text(safe(data.agentName, "Agente"), nameX, agentY + 22);

  // Contatti riga 2-col bottom
  const cY = A4_H - 38;
  if (data.agentPhone) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    setText(pdf, col.accent);
    pdf.text("TELEFONO", margin, cY);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(14);
    setText(pdf, "#1C1C1A");
    pdf.text(data.agentPhone, margin, cY + 7);
  }
  if (data.agentEmail) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    setText(pdf, col.accent);
    pdf.text("EMAIL", A4_W / 2, cY);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    setText(pdf, "#1C1C1A");
    pdf.text(shortenEmail(data.agentEmail, 28), A4_W / 2, cY + 7);
  }

  if (showProntoLabel) {
    pdf.setGState(new pdf.GState({ opacity: 0.4 }));
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    setText(pdf, "#1C1C1A");
    const wm = "prontoimmo.it";
    const wmW = pdf.getTextWidth(wm);
    pdf.text(wm, A4_W - margin - wmW, A4_H - 14);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }
}

function drawFooterMinimal(pdf, data, num) {
  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.2);
  pdf.setGState(new pdf.GState({ opacity: 0.3 }));
  pdf.line(18, A4_H - 18, A4_W - 18, A4_H - 18);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.6 }));
  const city = cleanCity(data.city);
  const label = [data.agencyName, city].filter(Boolean).join(" · ");
  pdf.text(label, 18, A4_H - 12);
  pdf.text(`${num} / 04`, A4_W - 18 - pdf.getTextWidth(`${num} / 04`), A4_H - 12);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}
